import { FC, ReactNode } from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { ArrowsLeftRight, CaretLeft, SquaresFour } from 'phosphor-react';

import Link from '~components/shared/link';
import { primaryColors } from '~components/theme/styles/colors';
import { defaultBorder } from '~components/theme/styles/mixins';
import { ENativeAsset } from '~config/types';

import { PageTitleBreadcrumbs } from '../breadcrumbs/pageTitleBreadcrumbs';
import { AssetIcon } from '../icons/assets';
import { VestaIcon, VestaIcons } from '../icons/vesta';

export type TextBlockVariant =
  | 'main'
  | 'secondary'
  | 'subsection'
  | 'arrow'
  | 'switch'
  | 'vault'
  | 'stability'
  | 'collectibles'
  | 'staking'
  | 'breadcrumbs'
  | 'vst'
  | 'boxlist';

export const TextBlock: FC<{
  id?: string;
  variant?: TextBlockVariant;
  primaryText: ReactNode;
  secondaryText?: ReactNode;
  descriptionText?: ReactNode;
  breadcrumbProps?: { previous: string };
}> = ({ id, variant = 'main', primaryText, secondaryText, descriptionText, breadcrumbProps }) => {
  if (variant === 'secondary') {
    return (
      <Grid id={id}>
        <Typography variant="h5" sx={{ color: primaryColors[100] }}>
          {primaryText}
        </Typography>
        <Typography variant="body2" sx={{ color: primaryColors[100], marginTop: '16px' }}>
          {secondaryText}
        </Typography>
      </Grid>
    );
  }
  if (variant === 'subsection') {
    return (
      <Grid id={id}>
        <Typography variant="h6" sx={{ color: primaryColors[400] }}>
          {primaryText}
        </Typography>
        <Typography variant="body3" sx={{ color: primaryColors[700], marginTop: '8px' }}>
          {secondaryText}
        </Typography>
      </Grid>
    );
  }

  if (['vault', 'stability', 'collectibles', 'staking'].includes(variant)) {
    const getIcon = () => {
      switch (variant) {
        case 'vault':
          return VestaIcons.VAULT;
        case 'stability':
          return VestaIcons.BADGE;
        case 'collectibles':
          return VestaIcons.RECEIPT;
        case 'staking':
          return VestaIcons.COIN;
        default:
          return VestaIcons.LOCK;
      }
    };
    return (
      <Grid id={id}>
        <Grid sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <VestaIcon icon={getIcon()} />
          <Grid>
            <Typography variant="label2" sx={{ color: primaryColors[100] }}>
              {primaryText}
            </Typography>
            <Typography variant="body3" sx={{ color: primaryColors[100] }}>
              {secondaryText}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (variant === 'arrow') {
    return (
      <Grid id={id}>
        <Grid sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <CaretLeft size={20} />
          <Typography variant="h6" sx={{ color: primaryColors[100] }}>
            {primaryText}
          </Typography>
        </Grid>
        <Typography variant="body3" sx={{ color: primaryColors[100], marginTop: '8px' }}>
          {secondaryText}
        </Typography>
      </Grid>
    );
  }

  if (variant === 'vst') {
    return (
      <Grid id={id}>
        <Grid sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box sx={{ borderRadius: '3px', padding: '12px', height: '48px' }}>
            <AssetIcon asset={ENativeAsset.VST} size={24} />
          </Box>
          <Grid>
            <Typography variant="h6" sx={{ color: primaryColors[100] }}>
              {primaryText}
            </Typography>
            <Typography variant="body3" sx={{ color: primaryColors[100] }}>
              {secondaryText}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (variant === 'boxlist') {
    return (
      <Grid id={id}>
        <Grid sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box sx={{ borderRadius: '3px', padding: '12px', height: '48px' }}>
            <SquaresFour size={24} color={primaryColors[100]} />
          </Box>
          <Grid>
            <Typography variant="h6" sx={{ color: primaryColors[100] }}>
              {primaryText}
            </Typography>
            <Typography variant="body3" sx={{ color: primaryColors[100] }}>
              {secondaryText}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (variant === 'switch') {
    return (
      <Grid id={id}>
        <Grid sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box sx={{ ...defaultBorder, borderRadius: '3px', padding: '12px', height: '48px' }}>
            <ArrowsLeftRight size={24} />
          </Box>
          <Grid>
            <Typography variant="h6" sx={{ color: primaryColors[100] }}>
              {primaryText}
            </Typography>
            <Typography variant="body3" sx={{ color: primaryColors[100] }}>
              {secondaryText}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (variant === 'breadcrumbs') {
    const { previous } = breadcrumbProps!;

    return (
      <Grid id={id} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Link href={previous}>
          <Grid sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CaretLeft color={primaryColors['700']} size={16} />
            <Typography
              variant="label3"
              sx={{
                color: primaryColors['700'],
                transition: 'color 0.05s ease',
                '&:hover': {
                  color: primaryColors[100],
                },
              }}
            >
              RETURN
            </Typography>
          </Grid>
        </Link>
        <Typography variant="h5" sx={{ color: primaryColors[100] }}>
          {primaryText}
        </Typography>
        <PageTitleBreadcrumbs />
      </Grid>
    );
  }

  return (
    <Grid id={id}>
      <Typography variant="h5" sx={{ color: primaryColors[100] }}>
        {primaryText}
      </Typography>
      {secondaryText && (
        <Typography variant="h6" sx={{ color: primaryColors[400], marginTop: '32px' }}>
          {secondaryText}
        </Typography>
      )}
      {descriptionText && (
        <Typography variant="body3" sx={{ color: primaryColors[700], marginTop: '8px' }}>
          {descriptionText}
        </Typography>
      )}
    </Grid>
  );
};
